import React from "react";

/**
 * Iconic Quantum Exposure radial dial.
 * Layered concentric arcs with glow — NOT a basic donut.
 * Renders the score, tier, and a sweeping arc whose color is tier-driven.
 */
export default function QuantumScoreDial({ score = 0, tier = "Low", size = 280 }) {
  const clamped = Math.max(0, Math.min(100, Number(score) || 0));
  // Traffic-light progression: Low/PQC-Ready = green; Moderate = yellow;
  // High = orange; Critical = red.
  const tierMap = {
    Critical: { color: "#FF2A2A", glow: "qt-dial-glow-red",    label: "CRITICAL" },
    High:     { color: "#FF8000", glow: "qt-dial-glow-orange", label: "HIGH" },
    Moderate: { color: "#FFD60A", glow: "qt-dial-glow-amber",  label: "MODERATE" },
    Low:      { color: "#1FDD83", glow: "qt-dial-glow-green",  label: "PQC READY" },
  };
  const cfg = tierMap[tier] || tierMap.Low;

  // Geometry — three concentric arcs, sweep ratio = score/100
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size * 0.46;
  const midR   = size * 0.40;
  const innerR = size * 0.34;
  const startAngle = 135; // degrees
  const fullSweep  = 270; // 270° gauge
  const valueSweep = (clamped / 100) * fullSweep;

  const polarToCart = (r, deg) => {
    const rad = (deg - 90) * (Math.PI / 180);
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };
  const arcPath = (r, sweep) => {
    if (sweep <= 0) return "";
    const start = polarToCart(r, startAngle);
    const end   = polarToCart(r, startAngle + sweep);
    const large = sweep > 180 ? 1 : 0;
    return `M ${start[0]} ${start[1]} A ${r} ${r} 0 ${large} 1 ${end[0]} ${end[1]}`;
  };

  return (
    <div
      data-testid="quantum-score-dial"
      style={{ width: size, height: size, position: "relative" }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Tracks */}
        <path d={arcPath(outerR, fullSweep)} stroke="rgba(22,36,64,0.6)" strokeWidth="2" fill="none" />
        <path d={arcPath(midR, fullSweep)}   stroke="rgba(22,36,64,0.6)" strokeWidth="6" fill="none" />
        <path d={arcPath(innerR, fullSweep)} stroke="rgba(22,36,64,0.6)" strokeWidth="2" fill="none" />

        {/* Tier-colored value arcs */}
        <g className={cfg.glow}>
          <path d={arcPath(outerR, valueSweep)} stroke={cfg.color} strokeWidth="2"  fill="none" strokeLinecap="round" opacity="0.55" />
          <path d={arcPath(midR,   valueSweep)} stroke={cfg.color} strokeWidth="6"  fill="none" strokeLinecap="round" />
          <path d={arcPath(innerR, valueSweep)} stroke={cfg.color} strokeWidth="2"  fill="none" strokeLinecap="round" opacity="0.55" />
        </g>

        {/* Tick marks — every 10 across the 270° range */}
        {Array.from({ length: 11 }).map((_, i) => {
          const ang = startAngle + (i / 10) * fullSweep;
          const [x1, y1] = polarToCart(outerR + 6, ang);
          const [x2, y2] = polarToCart(outerR + 12, ang);
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke="rgba(156,174,201,0.4)" strokeWidth="1" />
          );
        })}
      </svg>

      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          paddingBottom: 8,
        }}
      >
        <div className="qt-label" style={{ marginBottom: 4 }}>QUANTUM EXPOSURE</div>
        <div
          className="qt-mono"
          data-testid="score-value"
          style={{
            fontSize: size * 0.26,
            fontWeight: 700,
            lineHeight: 1,
            color: "#FFFFFF",
            letterSpacing: "-0.04em",
          }}
        >
          {clamped.toFixed(0)}
        </div>
        <div
          className="qt-mono"
          data-testid="score-tier"
          style={{
            marginTop: 8,
            fontSize: 12,
            letterSpacing: "0.25em",
            color: cfg.color,
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          {tier === "Low" ? "PQC READY" : tier.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
